import { useState } from 'react';
import { Card, Badge, Button } from '@/components/ui/primitives';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useLeadCategories } from '@/hooks/useLeadCategories';
import { addLeadCategory, mergeLeadCategory, resyncLeadCategories, updateLeadCategory } from '@/data/lead-categories';
import { categoryName, CATEGORY_SOURCE_LABEL as SOURCE_LABEL, type LeadCategoryItem, type LeadCategoryList } from '@/data/leads-model';
import { cn } from '@/lib/utils';

/* Settings › Lead categories (item 7, owner addition 05-10-2026: "add the option to
 * update/amend/add categories as well in the BookedCentral workspace").
 * The workspace's own list, seeded from the BookSesh marketplace categories
 * (booksesh.com) + the business types of the imported prospects. OWNER: add,
 * rename, archive / restore, merge (every lead of the merged category reads as the
 * target; undone by restoring) and "Re-sync from booksesh.com" (adds new marketplace
 * categories, never overwrites a local edit). A category is never deleted. Other
 * roles see the list read-only. Server-authoritative; every change is audited. */

const inputCls = 'h-10 min-w-0 rounded-md border border-border-default bg-surface px-3 text-sm text-text-primary';

export function LeadCategoriesPanel() {
  const { activeWorkspace } = useAuth();
  const isOwner = activeWorkspace?.role === 'owner';
  const { list, isLoading, error, refetch, replace } = useLeadCategories();
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [merging, setMerging] = useState<string | null>(null);
  const [mergeInto, setMergeInto] = useState('');

  if (isLoading) return <Card className="p-5 text-sm text-text-secondary">Loading categories…</Card>;
  if (error || !list) {
    return (
      <Card className="p-5 text-sm text-text-secondary">
        Could not load the categories. <button type="button" className="underline" onClick={() => void refetch()}>Try again</button>
      </Card>
    );
  }

  const run = async (fn: () => Promise<LeadCategoryList>, ok: string | ((l: LeadCategoryList) => string)) => {
    setBusy(true); setNotice(null);
    try {
      const l = await fn();
      replace(l);
      setNotice({ tone: 'ok', text: typeof ok === 'string' ? ok : ok(l) });
      return true;
    } catch (e) {
      setNotice({ tone: 'err', text: e instanceof ApiError ? e.message : 'Could not save — check your connection and try again.' });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const active = list.items.filter((i) => i.status === 'active' && !i.merged_into);
  const archived = list.items.filter((i) => i.status !== 'active' || i.merged_into);

  return (
    <div className="space-y-4" data-testid="lead-categories-panel">
      <Card className="p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 text-sm text-text-secondary">
            <p className="text-text-primary">Categories let you filter leads by what the business does (Pilates, Yoga, Hair…).</p>
            <p className="mt-1 text-xs">
              Each lead is categorised automatically from its business type and name{list.column_enabled ? "; you can also pick categories by hand in the lead's Edit sheet" : ' (picking them by hand per lead switches on after a one-off database update)'}.
              BookSesh's own categories come first (booksesh.com marketplace + events, snapshot {list.snapshot.version}), then categories derived from your leads, then Other.
              {list.synced_at ? ` Last re-sync ${list.synced_at.replace('T', ' ').replace('Z', ' UTC')}.` : ''}
            </p>
          </div>
          {isOwner ? (
            <Button variant="secondary" className="h-10 shrink-0" disabled={busy} data-testid="categories-resync"
              onClick={() => void run(async () => { const r = await resyncLeadCategories(); return r; }, (l) => {
                const r = (l as LeadCategoryList & { resync?: { source: string; added: LeadCategoryItem[]; reused?: Array<{ name: string }>; error?: string } }).resync;
                if (!r) return 'Re-synced.';
                const what = (r.added.length ? `Added ${r.added.map((a) => a.name).join(', ')}.` : 'No new BookSesh categories — nothing changed.') + (r.reused?.length ? ` Already here (kept, not duplicated): ${r.reused.map((x) => x.name).join(', ')}.` : '');
                return r.source === 'live' ? `Re-synced from booksesh.com. ${what}` : r.source === 'partial' ? `Re-synced; part of booksesh.com could not be read (${r.error ?? 'unavailable'}), the saved snapshot was used for it. ${what}` : `booksesh.com could not be read (${r.error ?? 'unavailable'}); used the saved snapshot. ${what}`;
              })}>
              Re-sync from booksesh.com
            </Button>
          ) : null}
        </div>
        {notice ? (
          <p className={cn('mt-3 rounded-md border px-3 py-2 text-xs', notice.tone === 'ok' ? 'border-success/20 bg-success/10 text-success' : 'border-danger/30 bg-danger/10 text-danger')} role={notice.tone === 'err' ? 'alert' : 'status'}>{notice.text}</p>
        ) : null}
        {isOwner ? (
          <form className="mt-4 flex flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); if (!newName.trim()) return; void run(() => addLeadCategory(newName.trim()), `Added "${newName.trim()}".`).then((ok) => { if (ok) setNewName(''); }); }}>
            <input className={cn(inputCls, 'flex-1')} placeholder="New category, e.g. Tattoo" value={newName} maxLength={60} onChange={(e) => setNewName(e.target.value)} aria-label="New category name" data-testid="category-new-name" />
            <Button type="submit" variant="primary" className="h-10" disabled={busy || !newName.trim()} data-testid="category-add">Add</Button>
          </form>
        ) : (
          <p className="mt-3 text-xs text-text-tertiary">Only the workspace owner can change categories.</p>
        )}
      </Card>

      <Card className="divide-y divide-border-subtle p-0" data-testid="category-list">
        {active.map((it) => (
          <div key={it.slug} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-slug={it.slug}>
            {editing === it.slug ? (
              <form className="flex min-w-0 flex-1 flex-wrap gap-2" onSubmit={(e) => { e.preventDefault(); void run(() => updateLeadCategory(it.slug, { name: editName }), `Renamed to "${editName.trim()}".`).then((ok) => { if (ok) setEditing(null); }); }}>
                <input className={cn(inputCls, 'flex-1')} value={editName} maxLength={60} onChange={(e) => setEditName(e.target.value)} aria-label={`Rename ${it.name}`} autoFocus />
                <Button type="submit" variant="primary" className="h-10" disabled={busy || !editName.trim()}>Save</Button>
                <Button type="button" variant="secondary" className="h-10" onClick={() => setEditing(null)}>Cancel</Button>
              </form>
            ) : (
              <div className="min-w-0">
                <span className="text-sm font-medium text-text-primary">{it.name}</span>
                <span className="ml-2 text-xs text-text-tertiary">{it.count ?? 0} lead{(it.count ?? 0) === 1 ? '' : 's'}</span>
                <span className="ml-2"><Badge tone={it.source === 'booksesh' || it.source === 'booksesh_events' ? 'ai' : 'neutral'}>{SOURCE_LABEL[it.source] ?? it.source}</Badge></span>
              </div>
            )}
            {isOwner && editing !== it.slug && it.slug !== 'other' ? (
              merging === it.slug ? (
                <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (!mergeInto) return; void run(() => mergeLeadCategory(it.slug, mergeInto), `Merged "${it.name}" into "${categoryName(mergeInto, list)}". Restore it any time to undo.`).then((ok) => { if (ok) { setMerging(null); setMergeInto(''); } }); }}>
                  <select className={inputCls} value={mergeInto} onChange={(e) => setMergeInto(e.target.value)} aria-label={`Merge ${it.name} into`}>
                    <option value="">Merge into…</option>
                    {active.filter((o) => o.slug !== it.slug).map((o) => <option key={o.slug} value={o.slug}>{o.name}</option>)}
                  </select>
                  <Button type="submit" variant="primary" className="h-10" disabled={busy || !mergeInto}>Merge</Button>
                  <Button type="button" variant="secondary" className="h-10" onClick={() => setMerging(null)}>Cancel</Button>
                </form>
              ) : (
                <div className="flex flex-wrap gap-3 text-xs">
                  <button type="button" className="text-text-secondary underline" onClick={() => { setEditing(it.slug); setEditName(it.name); }}>Rename</button>
                  <button type="button" className="text-text-secondary underline" onClick={() => { setMerging(it.slug); setMergeInto(''); }}>Merge</button>
                  <button type="button" className="text-text-secondary underline" disabled={busy} onClick={() => void run(() => updateLeadCategory(it.slug, { status: 'archived' }), `Archived "${it.name}". Leads keep it; it is hidden from new choices.`)}>Archive</button>
                </div>
              )
            ) : null}
          </div>
        ))}
      </Card>

      {archived.length ? (
        <Card className="p-0">
          <div className="px-4 pt-3 text-xs font-semibold uppercase tracking-wide text-text-tertiary">Archived / merged</div>
          <div className="divide-y divide-border-subtle">
            {archived.map((it) => (
              <div key={it.slug} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between" data-slug={it.slug}>
                <div className="min-w-0 text-sm text-text-secondary">
                  {it.name}
                  {it.merged_into ? <span className="ml-2 text-xs text-text-tertiary">merged into {categoryName(it.merged_into, list)}</span> : <span className="ml-2 text-xs text-text-tertiary">{it.count ?? 0} lead{(it.count ?? 0) === 1 ? '' : 's'}</span>}
                </div>
                {isOwner ? (
                  <button type="button" className="self-start text-xs text-text-secondary underline sm:self-auto" disabled={busy} onClick={() => void run(() => updateLeadCategory(it.slug, { status: 'active' }), `Restored "${it.name}".`)}>Restore</button>
                ) : null}
              </div>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
